import { useAuth } from "@hooks/useAuth";
import { useDataQuery } from "@hooks/useData/useDataQuery";
import { useDataMutation } from "@hooks/useData/useDataMutation";
import { selectData, supabase, softDelete } from "@utils/serverClient";
import { Location, InsertLocation, UpdateLocation, WithId } from "types";
import { useLocationRender } from "@components/shared/LocationsGate";
import { undefinedToNull } from "lib/utils";
import { useMusicLocation } from "@utils/queries";
import useGzLog from "@hooks/useGzLog";

export function useLocations() {
  const { organization } = useAuth();
  const { getAccessibleLocations } = useLocationRender();
  const queryKey = ["locations", organization?.id];
  const updateMusicLocation = useMusicLocation.update()
  const deleteMusicLocation = useMusicLocation.delete()
  const { log } = useGzLog()

  const { data, status, refetch } = useDataQuery({
    queryKey: queryKey,
    queryFn: async () => {
      if (!organization?.id) return [];
      const { data, error } = await selectData({
        table: "location",
        columns: "*",
      })
        .eq("org_id", organization.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const createMutation = useDataMutation({
    mutationFn: async (formData: WithId<InsertLocation>) => {
      const { data, error } = await supabase
        .from("location")
        .insert({ ...formData, org_id: organization?.id })
        .select()
        .single();
      if (error) throw new Error(error.message);
      return data;
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to add location.",
    optimisticUpdateFn: (oldData: Location[] | undefined, newLocation) => {
      const optimisticLocation: Location = {
        ...undefinedToNull(newLocation),
        created_at: new Date().toISOString(),
        deleted: false,
        updated_at: null,
        org_id: organization?.id || "",
      };
      return [optimisticLocation, ...(oldData || [])];
    },
    options: {
      onSuccess: (d) => {
        log({
          eventtype: "LocationCreated",
          name: "LocationCreated",
          payload: {
            locationId: d?.id ?? "undefined",
            locationName: d?.name ?? "no location",
          },
        });
      },
      onError: (error, { name }) => {
        log({
          eventtype: "LocationCreateFailed",
          name: "LocationCreateFailed",
          payload: {
            locationName: name ?? "undefined",
            errorMessage: error.message,
          },
        });
      },
    }
  });

  const updateMutation = useDataMutation({
    mutationFn: async (formData: UpdateLocation) => {
      const { data, error } = await supabase
        .from("location")
        .update(formData)
        .match({ id: formData.id })
        .select()
        .single();
      if (error) throw new Error(error.message);
      return data;
    },
    options: {
      onSuccess(data, variables) {
        if (data?.id_code && "name" in variables) {
          updateMusicLocation.mutate({ name: variables.name, id: data.id_code });
        }

        log({
          eventtype: "LocationUpdated",
          name: "LocationUpdated",
          payload: {
            locationId: data?.id ?? "undefined",
            locationName: data?.name ?? "undefined",
          },
        });
      },
      onError: (error, variables) => {
        log({
          eventtype: "LocationUpdateFailed",
          name: "LocationUpdateFailed",
          payload: {
            locationId: variables.id ?? "undefined",
            locationName: variables.name ?? "undefined",
            errorMessage: error.message,
          },
        });
      },
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to update location.",
    optimisticUpdateFn: (oldData: Location[] | undefined, updatedLocation) => {
      if (!oldData) return [];
      return oldData.map((location) =>
        location.id === updatedLocation.id ? { ...location, ...updatedLocation } : location,
      );
    },
  });

  const deleteMutation = useDataMutation({
    mutationFn: async (id: Location["id"]) => {
      const location = data?.find(l => l.id === id);
      console.log('deleted location', location)
      if (location?.id_code) {
        deleteMusicLocation.mutateAsync(location.id_code);
      }
      const { error } = await softDelete({
        table: "location",
      }).match({ id });
      if (error) throw new Error(error.message);
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to delete location.",
    optimisticUpdateFn: (oldData: Location[] | undefined, id) => {
      return oldData?.filter((location) => location.id !== id) ?? [];
    },
  });

  const getLocationNameById = (id: string): string => {
    const _loc = (data ?? []).find((location) => location.id === id);
    return _loc?.name ?? "N/A";
  };

  return {
    locations: getAccessibleLocations(data ?? []),
    locationsFetchStatus: status,
    getLocations: refetch,
    getLocationNameById,
    createLocation: createMutation.mutate,
    isCreatingLocation: createMutation.isPending,
    updateLocation: updateMutation.mutate,
    isUpdatingLocation: updateMutation.isPending,
    deleteLocation: deleteMutation.mutate,
    isDeletingLocation: deleteMutation.isPending,
  };
}